import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Input,
  Fade,
  Tooltip,
  Paper,
  CircularProgress,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SchoolIcon from "@mui/icons-material/School";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import { BASE_URL } from "../../constants";
import { useRecoilValue } from "recoil";
import { authState } from "../../recoil/authAtom";
import { FILE_RULES, validateFile } from "../../utils/fileValidation";
import { toast } from "react-toastify";

const INITIAL_STATE = {
  name: "",
  qualifications: [""],
  regId: "",
  note: "",
  consultFees: 0,
  isConsultant: false,
  isSignatory: false,
  signatoryCat: "",
  signFile: null,
  signHeader: "",
};

const DoctorRegForm = () => {
  const user = useRecoilValue(authState);
  const [doctorData, setDoctorData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setDoctorData({
      ...doctorData,
      [e.target.name]: e.target.value,
    });
  };

  // const handleSignFileChange = (e) => {
  //   setDoctorData({
  //     ...doctorData,
  //     signFile: e.target.files[0],
  //   });
  // };
  const handleSignFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await validateFile(file, FILE_RULES.signature);
    if (!result.valid) {
      alert(result.error);
      e.target.value = "";
      return;
    }
    setDoctorData({ ...doctorData, signFile: file });
  };

  const handleQualificationChange = (index, value) => {
    const updatedQualifications = [...doctorData.qualifications];
    updatedQualifications[index] = value;
    setDoctorData({ ...doctorData, qualifications: updatedQualifications });
  };

  const addQualification = () => {
    setDoctorData({
      ...doctorData,
      qualifications: [...doctorData.qualifications, ""],
    });
  };

  const removeQualification = (index) => {
    const updatedQualifications = doctorData.qualifications.filter(
      (_, i) => i !== index,
    );
    setDoctorData({ ...doctorData, qualifications: updatedQualifications });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!doctorData.name) {
      newErrors.name = "Doctor name is required.";
    }
    if (!doctorData.qualifications.some((q) => q.trim())) {
      newErrors.qualifications = "At least one qualification is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit1 = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      const formData = new FormData();
      for (const key in doctorData) {
        if (Array.isArray(doctorData[key])) {
          doctorData[key].forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        } else {
          formData.append(key, doctorData[key]);
        }
      }

      try {
        const response = await fetch(
          `${BASE_URL}api/user/common/doctor-register`,
          {
            method: "POST",
            headers: {
            },
            body: formData,
          },
        );

        const data = await response.json();

        if (data.success) {
          toast.success(data.message);
          setDoctorData(INITIAL_STATE);
          window.location.reload();
        } else {
          toast.error("Error: " + data.message);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong! Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();

      // 🔥 Append regular doctor fields
      for (const key in doctorData) {
        if (key === "signFile" || key === "appointmentFile") continue; // these are files

        if (Array.isArray(doctorData[key])) {
          doctorData[key].forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        } else {
          formData.append(key, doctorData[key]);
        }
      }

      // 🔥 Append Signature file (signFile)
      if (doctorData.signFile) {
        formData.append("signFile", doctorData.signFile);
      }

      // 🔥 Append Appointment Template PDF (appointmentFile)
      if (doctorData.appointmentFile) {
        formData.append("appointmentFile", doctorData.appointmentFile);
      }

      const response = await fetch(
        `${BASE_URL}api/user/common/doctor-register`,
        {
          method: "POST",
          headers: {
            // ❌ DO NOT SET Content-Type → Browser sets it with boundary for FormData
          },
          body: formData,
        },
      );

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setDoctorData(INITIAL_STATE);
        // window.location.reload();
      } else {
        toast.error("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  //
  return (
    <>
      {user?._doc?.permissions?.doctorManagement?.create && (
        <Accordion
          sx={{
            borderRadius: "14px !important",
            background: "#ffffff",
            border: "1px solid #e5e9ef",
            boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
            "&:before": { display: "none" },
            "&.Mui-expanded": {
              margin: "0",
              boxShadow: "0 8px 28px rgba(10,31,68,0.1)",
            },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #0f6e56, #1d9e75)",
                  border: "1px solid rgba(185,58,207,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ExpandMoreIcon sx={{ color: "#ffffff", fontSize: "1.1rem" }} />
              </Box>
            }
            sx={{
              background: "#ffffff",
              borderBottom: "1px solid #e5e9ef",
              px: 2.5,
              py: 1,
              minHeight: "56px !important",
              "&.Mui-expanded": { minHeight: "56px !important" },
              "& .MuiAccordionSummary-content": { margin: "12px 0 !important" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "9px",
                  background: "linear-gradient(135deg, #0f6e56, #1d9e75)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                }}
              >
                <PersonAddIcon sx={{ color: "#ffffff", fontSize: "1rem" }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    color: "#0a1f44",
                    letterSpacing: "-0.2px",
                    lineHeight: 1.2,
                  }}
                >
                  Register Doctor
                </Typography>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#94a3b8", mt: "1px" }}
                >
                  Fill in the details below to register a new doctor
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ background: "#f8fafc", p: 2.5 }}>
            <Box component="form">
              <Grid container spacing={2}>
                {/* Doctor Name */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Doctor Name <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="name"
                    placeholder="Full name"
                    value={doctorData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={dFieldStyle}
                  />
                </Grid>

                {/* Registration ID */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Registration ID <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="regId"
                    placeholder="e.g. MCI-12345"
                    value={doctorData.regId}
                    onChange={handleChange}
                    error={!!errors.regId}
                    helperText={errors.regId}
                    sx={dFieldStyle}
                  />
                </Grid>

                {/* Note */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Note <Opt />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="note"
                    placeholder="Internal note"
                    value={doctorData.note}
                    onChange={handleChange}
                    sx={dFieldStyle}
                  />
                </Grid>

                {/* Consultation Fees */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Consultation Fees <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    name="consultFees"
                    placeholder="₹ 0"
                    value={doctorData.consultFees}
                    onChange={handleChange}
                    error={!!errors.consultFees}
                    helperText={errors.consultFees}
                    sx={dFieldStyle}
                  />
                </Grid>

                {/* Qualifications */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: "14px",
                      background: "#ffffff",
                      border: "1px solid #e5e9ef",
                      boxShadow: "0 1px 6px rgba(10,31,68,0.05)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "7px",
                          background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SchoolIcon
                          sx={{ color: "#fff", fontSize: "0.85rem" }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#0a1f44",
                        }}
                      >
                        Qualifications &amp; Experience
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.65rem", color: "#94a3b8", ml: 0.5 }}
                      >
                        (max 6)
                      </Typography>
                    </Box>

                    <Grid container spacing={1.5}>
                      {doctorData.qualifications.map((qualification, index) => (
                        <Fade in timeout={600} key={index}>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <TextField
                              fullWidth
                              size="small"
                              label={`Qualification ${index + 1}`}
                              value={qualification}
                              onChange={(e) =>
                                handleQualificationChange(index, e.target.value)
                              }
                              error={!!errors.qualifications}
                              sx={dFieldStyle}
                            />
                            <Tooltip title="Remove" arrow>
                              <span>
                                <IconButton
                                  onClick={() => removeQualification(index)}
                                  disabled={
                                    doctorData.qualifications.length === 1
                                  }
                                  size="small"
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: "8px",
                                    background: "rgba(239,68,68,0.08)",
                                    border: "1px solid rgba(239,68,68,0.2)",
                                    color: "#dc2626",
                                    flexShrink: 0,
                                    "&:hover": {
                                      background: "rgba(239,68,68,0.15)",
                                      borderColor: "rgba(239,68,68,0.4)",
                                    },
                                    "&.Mui-disabled": { opacity: 0.35 },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Grid>
                        </Fade>
                      ))}
                    </Grid>

                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Tooltip title="Add Qualification" arrow>
                        <span>
                          <IconButton
                            onClick={addQualification}
                            disabled={doctorData.qualifications.length >= 6}
                            size="small"
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "8px",
                              background:
                                doctorData.qualifications.length >= 6
                                  ? "#f1f5f9"
                                  : "linear-gradient(135deg,#0f6e56,#1d9e75)",
                              color: "white",
                              "&:hover": {
                                background:
                                  doctorData.qualifications.length >= 6
                                    ? "#f1f5f9"
                                    : "linear-gradient(135deg,#064e3b,#0f6e56)",
                                transform: "scale(1.06)",
                              },
                              "&.Mui-disabled": { opacity: 0.4 },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <AddIcon sx={{ fontSize: "0.95rem" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          fontStyle: "italic",
                        }}
                      >
                        {doctorData.qualifications.length}/6 qualifications
                        added
                      </Typography>
                    </Box>

                    {errors.qualifications && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 1.5,
                          background: "rgba(239,68,68,0.07)",
                          border: "1px solid rgba(239,68,68,0.2)",
                          borderRadius: "8px",
                          px: 1.25,
                          py: 0.75,
                        }}
                      >
                        <WarningAmberIcon
                          sx={{ fontSize: "0.85rem", color: "#dc2626" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#dc2626",
                          }}
                        >
                          {errors.qualifications}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Is Consultant */}
                <Grid item xs={12} sm={4}>
                  <Typography sx={dLabelStyle}>Is Consultant?</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={doctorData.isConsultant}
                      name="isConsultant"
                      onChange={handleChange}
                      sx={dSelectStyle}
                    >
                      <MenuItem value={true} sx={{ fontSize: "0.875rem" }}>
                        Yes
                      </MenuItem>
                      <MenuItem value={false} sx={{ fontSize: "0.875rem" }}>
                        No
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Is Signatory */}
                <Grid item xs={12} sm={4}>
                  <Typography sx={dLabelStyle}>Is Signatory?</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={doctorData.isSignatory}
                      name="isSignatory"
                      onChange={handleChange}
                      sx={dSelectStyle}
                    >
                      <MenuItem value={true} sx={{ fontSize: "0.875rem" }}>
                        Yes
                      </MenuItem>
                      <MenuItem value={false} sx={{ fontSize: "0.875rem" }}>
                        No
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Signatory Category */}
                <Grid item xs={12} sm={4}>
                  <Typography sx={dLabelStyle}>Signatory Category</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={doctorData.signatoryCat}
                      name="signatoryCat"
                      onChange={handleChange}
                      sx={dSelectStyle}
                    >
                      {[
                        "Pathology",
                        "Radiology",
                        "Microbiology",
                        "Biochemistry",
                      ].map((c) => (
                        <MenuItem
                          key={c}
                          value={c}
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sign Header */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Sign Header <Opt />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={1}
                    name="signHeader"
                    placeholder="Designation, qualification line…"
                    value={doctorData.signHeader}
                    onChange={handleChange}
                    sx={dFieldStyle}
                  />
                </Grid>

                {/* Sign upload */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={dLabelStyle}>
                    Signature Image <Opt />
                  </Typography>
                  <Box
                    sx={{
                      border: "1.5px dashed rgba(185,58,207,0.3)",
                      borderRadius: "10px",
                      p: 1.5,
                      background: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "9px",
                        background:
                          "linear-gradient(135deg,rgba(42,84,166,0.1),rgba(185,58,207,0.08))",
                        border: "1px solid rgba(185,58,207,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: "1rem", color: "#b93acf" }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#64748b", mb: 0.5 }}
                      >
                        Upload signature image
                      </Typography>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleSignFileChange}
                        style={{
                          fontSize: "0.75rem",
                          color: "#0a1f44",
                          width: "100%",
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Appointment PDF */}
                {doctorData.isConsultant && (
                  <Grid item xs={12}>
                    <Typography sx={dLabelStyle}>
                      Appointment Template <Opt />
                    </Typography>
                    <Box
                      sx={{
                        border: "1.5px dashed rgba(42,84,166,0.25)",
                        borderRadius: "10px",
                        p: 1.5,
                        background: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "9px",
                          background: "rgba(239,68,68,0.08)",
                          border: "1px solid rgba(239,68,68,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <PictureAsPdfIcon
                          sx={{ fontSize: "1rem", color: "#dc2626" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            borderColor: "rgba(42,84,166,0.25)",
                            color: "#2a54a6",
                            background: "rgba(42,84,166,0.05)",
                            "&:hover": {
                              background: "rgba(42,84,166,0.1)",
                              borderColor: "#2a54a6",
                            },
                          }}
                        >
                          Choose PDF
                          <input
                            type="file"
                            hidden
                            accept="application/pdf"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              validateFile(file, FILE_RULES.pdf).then((result) => {
                                if (result.valid)
                                  setDoctorData({ ...doctorData, appointmentFile: file });
                                else {
                                  e.target.value = "";
                                  toast.error(result.error);
                                }
                              });
                            }}
                          />
                        </Button>
                        {doctorData.appointmentFile && (
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "#64748b",
                              mt: 0.5,
                            }}
                          >
                            📄{" "}
                            <strong>{doctorData.appointmentFile.name}</strong>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Submit */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pt: 0.5,
                    }}
                  >
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={
                        loading ? null : (
                          <PersonAddIcon sx={{ fontSize: "1rem !important" }} />
                        )
                      }
                      sx={{
                        background: loading
                          ? "#e5e9ef"
                          : "linear-gradient(135deg, #2a54a6, #b93acf)",
                        color: loading ? "#94a3b8" : "#ffffff",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        textTransform: "none",
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        boxShadow: loading
                          ? "none"
                          : "0 4px 14px rgba(185,58,207,0.3)",
                        "&:hover": {
                          background: "linear-gradient(135deg,#1e3d8a,#a02db8)",
                          boxShadow: "0 6px 20px rgba(185,58,207,0.4)",
                        },
                        "&:disabled": { background: "#e5e9ef" },
                        transition: "all 0.25s ease",
                      }}
                    >
                      {loading ? "Submitting…" : "Register Doctor"}
                    </Button>
                    {loading && (
                      <CircularProgress size={18} sx={{ color: "#b93acf" }} />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default DoctorRegForm;

const dLabelStyle = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#0a1f44",
  letterSpacing: "0.2px",
  mb: 0.6,
};

const dFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#ffffff",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#e5e9ef" },
    "&:hover fieldset": { borderColor: "#2a54a6" },
    "&.Mui-focused fieldset": { borderColor: "#b93acf", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#b93acf" },
};

const dSelectStyle = {
  borderRadius: "10px",
  background: "#ffffff",
  fontSize: "0.875rem",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e9ef" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2a54a6" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b93acf",
    borderWidth: "1.5px",
  },
};

const Req = () => (
  <Box component="span" sx={{ color: "#b93acf", ml: "2px", fontWeight: 700 }}>
    *
  </Box>
);
const Opt = () => (
  <Box
    component="span"
    sx={{ color: "#94a3b8", fontWeight: 400, ml: "3px", fontSize: "0.62rem" }}
  >
    (optional)
  </Box>
);
